import * as React from 'react';
import { DateTime } from 'luxon';
import { Icon } from '@teamsnap/snap-ui';
import { Button } from '@teamsnap/teamsnap-ui';
import { useMutation, useQuery } from '@apollo/client';

// Local Imports
import DiscountItem from './DiscountItem';
import DiscountDrawerForm from './DiscountDrawerForm/DiscountDrawerForm';
import { RegistrationFormAdjustment } from './AddOnFees/models';
import { getDSTOffsetHours, getOrgTimezoneOffset } from './date-utils';
import * as UpdateFormAdjustment from 'core/gql/mutations/update-form-adjustment';
import * as RegistrationFormAdjustments from 'core/gql/queries/registration-form-adjustments';
import * as ShowOrganization from 'core/gql/queries/show-organizations';

type Props = {
  formId: number;
  orgId: number;
  adjustments: RegistrationFormAdjustment[];
};

const Discounts: React.FC<Props> = ({ formId, orgId, adjustments }) => {
  const [showDrawer, setShowDrawer] = React.useState<boolean>(false);
  const [editingDiscount, setEditingDiscount] = React.useState<RegistrationFormAdjustment | null | undefined>();

  const refetch = {
    refetchQueries: [{ query: RegistrationFormAdjustments.QUERY, variables: { formId: +formId } }],
  };

  const [updateFormAdjustment, { loading: isUpdating }] = useMutation(UpdateFormAdjustment.MUTATION, refetch);

  const showOrgQuery = ShowOrganization.QUERY;

  const getOrganization = useQuery(showOrgQuery, {
    variables: { organizationId: +orgId },
    skip: !orgId,
  });

  const organization = getOrganization.data?.showOrganization;

  const determineDateFromTimezone = (date: string) => {
    const orgTimezoneUTCOffset = getOrgTimezoneOffset(organization?.timezoneIana);

    const dateTime = DateTime.fromISO(date, { setZone: true }).minus({
      hours: getDSTOffsetHours(new Date(date), orgTimezoneUTCOffset),
    });

    const formattedDateTime = dateTime.setLocale('en-US').toLocaleString(DateTime.DATE_SHORT);

    return new Date(formattedDateTime);
  };

  const handleDrawerClose = () => {
    setEditingDiscount(undefined);
  };

  const discounts = adjustments.filter((adjustment) => adjustment.formAdjustmentDefinition.type === 'discount');

  const renderDiscounts = (discounts: RegistrationFormAdjustment[]) => (
    <>
      <section data-testid="discounts-list" className="sui-py-1">
        {discounts.map((discount, index) => (
          <DiscountItem
            index={index}
            onToggle={(discount) => toggleDiscount(discount)}
            onEdit={(discount) => setEditingDiscount(discount)}
            isLoading={isUpdating}
            discount={discount}
            key={discount.id}
            determineDateFromTimezone={determineDateFromTimezone}
          />
        ))}
      </section>
      <div className="sui-flex sui-mt-1">
        <span className="sui-text-skyblue-40">
          <Icon name="info" size="s" />
        </span>

        <span data-testid="discounts-info" className="sui-body-dense sui-text-gray-30 sui-ml-3">
          <ul>
            <li className="sui-mb-1">
              Discounts can be stacked. Registrants can use one or more discounts when checking out.
            </li>
            <li className="sui-mb-1">
              When stacked, "custom amount" discounts are applied first, and then "% off" discounts are applied. This is
              true regardless of which order the discounts are input by the registrant.
            </li>
            <li className="sui-mb-1">
              If multiple "% off" discounts are applied, the "% off" for each code is calculated off of the original
              cart total, not the remaining balance after applying the first code.
            </li>
            <li className="sui-mb-1">
              While registrants may have discounts that when applied would exceed the entire cart total, checkout totals
              will never result in a negative balance due.
            </li>
          </ul>
        </span>
      </div>
    </>
  );

  const toggleDiscount = async (discount: RegistrationFormAdjustment) => {
    const formAdjustmentPayload = {
      ...discount,
      formAdjustmentDefinitionId: discount.formAdjustmentDefinition.id,
      enabled: !discount.enabled,
    };
    await updateFormAdjustment({ variables: formAdjustmentPayload });
  };

  React.useEffect(() => {
    // Assure that discount is updated prior to mounting form.
    if (editingDiscount === undefined) {
      setShowDrawer(false);
    } else {
      setShowDrawer(true);
    }
  }, [editingDiscount]);

  return (
    <React.Fragment>
      <DiscountDrawerForm
        currentDiscounts={discounts}
        isOpen={showDrawer}
        onClose={handleDrawerClose}
        discount={editingDiscount}
        discounts={discounts}
        formId={formId}
        organization={organization}
        currency={organization?.currency || ''}
        currencySymbol={organization?.currencySymbol || ''}
        determineDateFromTimezone={determineDateFromTimezone}
      />

      <div id="discounts" className="sui-flex sui-flex-col sui-pb-6">
        <div className="sui-flex sui-self-end sui-pb-2">
          <Button
            testId="discounts--add-discount"
            type="button"
            mods="sui-px-4"
            color="primary"
            onClick={() => setEditingDiscount(null)}
            icon="plus"
            style={{ height: 44 }}
          >
            Add discount
          </Button>
        </div>

        {discounts.length > 0 ? renderDiscounts(discounts) : <p>No discounts created.</p>}
      </div>
    </React.Fragment>
  );
};

export default Discounts;
